import {
  buildDatasetUploadResetReq,
  cancelDatasetUpload,
  completeDatasetUpload,
  declareDatasetUploadFiles,
  deleteDatasetUploadStagedFile,
  getDatasetUpload,
  heartbeatDatasetUpload,
  initDatasetUpload,
  listDatasetUploadStagedFiles,
  prefillBandDiagnosticsEnabled,
  prefillReservedLabels,
  reprocessDatasetUpload,
  resetDatasetUpload,
  setDatasetUploadReservedLabels,
  type DatasetUploadCompleteReq,
  type DatasetUploadJob,
  type DatasetUploadStatus,
  type StagedFile,
} from "@/api/datasetUpload";
import type { ScannedFile } from "./folderScan";
import { aggregateProgress } from "./progress";
import { runPutPool, UploadConnectionLostError } from "./putPool";
import { diffStagedFiles, fetchAllStagedFiles, type StagedDiff } from "./reconcile";
import { assertStagingObjectNamesWithinLimit } from "./stagingObjectName";
import { UploadTicketStore } from "./ticketStore";
import { delayUnlessAborted, throwIfUploadAborted } from "./uploadAbort";
import type {
  DatasetUploadApi,
  UploadConnectionState,
  UploadEngineConfig,
  UploadEngineJobStatus,
  UploadEngineSnapshot,
  UploadFileState,
  UploadFinalizeState,
  UploadJobMetadata,
  UploadJobSpec,
  UploadJobView,
} from "./types";
import {
  formatUploadJobFailure,
  requiresUploadSourceChange,
  isCompleteOverLimit,
  isCompleteStagingMismatch,
  isUploadLeaseHeld,
  resolveUploadErrorMessage,
  uploadBlockedMessage,
} from "./uploadErrors";

/**
 * 앱 범위 업로드 엔진. files 모드 업로드 실행의 단일 소유자다.
 *
 * Owns:
 * - 잡 큐와 잡/파일 상태 전이. 전송 슬롯은 1개이고 순차로 쓴다. 총 대역폭이 고정이라 잡을 병렬로
 *   실행해도 이득이 없다.
 * - init→declare→PUT 풀→complete 체인, 재개(probe diff)·reset·reprocess 경로, lease heartbeat,
 *   complete 이후 서버 파이프라인 폴링.
 * - 단절 대응: 풀이 단절을 기다리는 동안 잡의 connection 을 waiting 으로 둔다. 단절이 한도까지
 *   이어지면 잡을 failed 가 아니라 paused 로 둔다(connection 은 lost).
 * - 실패 파일의 자동 재시도: PUT 풀이 끝나면 실패 파일만 다시 올린다(autoRetryRounds).
 * - complete 직전 확인: staged 건수가 선택 폴더와 같을 때만 complete 한다. 다르면 빠진 파일을 다시
 *   올린다(stagingRepairRounds). complete 본문에는 기대 파일 수와 총 바이트를 싣는다.
 * - autoComplete 정책: 전송이 실패 없이 끝나고 flag 가 켜져 있으면 즉시 complete 한다. 아니면
 *   awaitingComplete 에서 명시적 complete() 를 기다린다. awaitingComplete/completing/processing 은
 *   전송 슬롯을 차지하지 않는다(다음 잡이 바로 전송을 시작한다).
 *
 * Does not own:
 * - 입력 검증(다이얼로그), 폴더 스캔 실행(UI 가 folderScan 호출), 화면 문구·버튼 표시(UI).
 *
 * document 수명 동안 유지되는 싱글턴이라 SPA 라우팅 이동에도 업로드가 끊기지 않는다. 새로고침으로
 * 사라진 큐는 서버 잡(PENDING_UPLOAD)이 SoT 다. 현황 페이지의 재개 경로가 그 잡을 이어받는다.
 */

const defaultConfig: UploadEngineConfig = {
  // 기본 위치가 워커와 같은 리전이라 국내 업로더의 목적지는 태평양 건너편이다. RTT 가 처리량을
  // 정하는 구간이라 3 으로는 회선을 채우지 못한다(ZERG-430 이 직행 기본값의 선행 조건으로 정한 값).
  fileConcurrency: 12,
  declareBatchSize: 200,
  ticketExpiryMarginMs: 120_000,
  putMaxAttempts: 3,
  putBackoffBaseMs: 1_000,
  putStallTimeoutMs: 60_000,
  reconnectBackoffMaxMs: 30_000,
  offlineGraceMs: 5_000,
  // 유예(5초)보다 짧게 둔다. 그래야 정상 전송의 바이트가 유예 안에 증거가 된다.
  progressEvidenceAfterMs: 2_000,
  putMaxInterruptions: 5,
  maxOfflineMs: 600_000,
  autoRetryRounds: 3,
  stagingRepairRounds: 2,
  pruneOrphans: true,
  // 서버 lease TTL(90s)보다 짧게 둔다. 백그라운드 탭 throttling(최악 ~60s)에서도 만료 전에 갱신된다.
  heartbeatIntervalMs: 30_000,
  pollIntervalMs: 5_000,
  probePageSize: 100,
};

// 기본 API 바인딩. 테스트는 생성자 주입으로 대체한다.
const liveDatasetUploadApi: DatasetUploadApi = {
  init: initDatasetUpload,
  get: getDatasetUpload,
  declareFiles: declareDatasetUploadFiles,
  listStagedFiles: listDatasetUploadStagedFiles,
  deleteStagedFile: deleteDatasetUploadStagedFile,
  complete: completeDatasetUpload,
  cancel: cancelDatasetUpload,
  reset: resetDatasetUpload,
  reprocess: reprocessDatasetUpload,
  heartbeat: heartbeatDatasetUpload,
  setReservedLabels: setDatasetUploadReservedLabels,
};

const abortedMessage = "업로드를 일시정지했습니다.";
const cancelledMessage = "업로드가 취소되었습니다.";
const stagingCountMismatchMessage =
  "서버에 올라간 파일 수가 선택한 폴더와 다릅니다. 다시 시도해 주세요.";
const unsettledFilesMessage = "올라가지 않은 파일이 남아 완료하지 않았습니다. 다시 시도해 주세요.";
// 단절 한도를 분 단위로 적는다. 한도는 설정값이라 문구에 숫자를 고정하지 않는다.
function connectionLostMessage(maxOfflineMs: number): string {
  const minutes = Math.max(1, Math.round(maxOfflineMs / 60_000));
  return `네트워크 연결이 ${minutes}분 동안 돌아오지 않았습니다. 연결을 확인한 뒤 재개해 주세요.`;
}

const conflictMessage =
  "같은 이름의 Dataset 업로드가 이미 존재합니다. 이름을 바꾸거나 해당 업로드를 확인해 주세요.";

const terminalStatuses: readonly UploadEngineJobStatus[] = [
  "completed",
  "failed",
  "cancelled",
  "blocked",
];

// 전송 단계(브라우저를 닫으면 실제로 끊기는 구간). beforeunload 가드와 위젯 표시 판단에 쓴다.
const clientPendingStatuses: readonly UploadEngineJobStatus[] = [
  "queued",
  "initializing",
  "reconciling",
  "uploading",
  "completing",
];

const retryableStatuses: readonly UploadEngineJobStatus[] = ["failed", "blocked", "cancelled"];

// 완료 판정 기준은 finalize 가 설정한 `done` 이다. finalize 가 카탈로그 확정과 진단 잡 enqueue 를 한
// 트랜잭션으로 끝낸다. 그래서 그 뒤에 사용자가 기다릴 업로드 단계가 없다. 진단은 별도 잡이라
// 업로드와 관계없이 실행된다.
const serverSuccessStatuses: readonly DatasetUploadStatus[] = ["done", "duplicate_skipped"];
const serverFailedStatuses: readonly DatasetUploadStatus[] = ["failed", "conflict"];

type StopIntent = "pause" | "cancel";

type InternalJob = {
  id: string;
  spec: UploadJobSpec;
  status: UploadEngineJobStatus;
  // finalize(complete) 단계 상태. 전송이 실패 없이 끝난 뒤 항상 실행하고, 실패하면 재시도 대상 아이템이 된다.
  finalize: UploadFinalizeState;
  uploadJob: DatasetUploadJob | null;
  files: UploadFileState[];
  // 전송 중 파일의 누적 바이트(파일 기준). settled(uploaded/skipped)와 합쳐 진행률을 계산한다.
  inFlightLoaded: Map<string, number>;
  orphans: StagedFile[];
  progress: UploadJobView["progress"];
  errorMessage: string | null;
  abortController: AbortController | null;
  // pause 와 cancel 이 abort 를 함께 쓰므로 의도를 따로 기록해 abort 후 분기한다.
  stopIntent: StopIntent | null;
  // init/reset/reconcile 을 이미 마쳤는지. 일시정지 후 재개가 서버 잡을 중복 생성하거나
  // reset 을 다시 실행해 staged 바이트를 잃는 것을 막는다.
  initialized: boolean;
  // 서버가 complete 를 기대값 불일치로 거부했는지. true 면 다음 전송은 건수 확인을 건너뛰고 staged 전체
  // 목록을 대조한다. 건수가 같고 크기만 다른 파일은 건수 확인으로 찾을 수 없다.
  stagingDiffRequired: boolean;
  // finalize 재시도(retryFinalize)가 complete 본문에 싣는 기대값이다. 실패한 complete 가 실은 값을
  // 그대로 쓴다. 서버가 용량 초과(413)로 거부했으면 비운다.
  finalizeExpectation: DatasetUploadCompleteReq;
  connection: UploadConnectionState;
};

export class UploadEngine {
  private readonly api: DatasetUploadApi;
  private readonly config: UploadEngineConfig;
  private readonly listeners = new Set<() => void>();
  private jobs: InternalJob[] = [];
  private activeJobId: string | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  // 현재 하트비트 대상 잡 uuid(활성 전송). visibilitychange 즉시 갱신에 쓴다.
  private heartbeatUuid: string | null = null;
  // 하트비트가 lease 인수(409)를 감지해 abort 를 호출했으면 true. abort reject 를 blocked 로 처리한다.
  private leaseBlocked = false;
  private readonly pollTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private snapshot: UploadEngineSnapshot = { jobs: [], activeJobId: null, hasPendingWork: false };
  private sessionActive = false;
  // 종료 전 비동기 응답이 다음 로그인 세션의 전역 상태를 바꾸지 못하게 하는 세대 번호.
  private sessionGeneration = 0;

  constructor(deps: { api?: DatasetUploadApi; config?: Partial<UploadEngineConfig> } = {}) {
    this.api = deps.api ?? liveDatasetUploadApi;
    this.config = { ...defaultConfig, ...deps.config };
  }

  /** 로그인 세션에서 업로드 큐를 활성화한다. 같은 세션의 중복 시작은 무시한다. */
  startSession(): void {
    if (this.sessionActive) {
      return;
    }
    this.sessionActive = true;
    // 탭이 다시 보이면 lease 를 즉시 갱신한다(백그라운드 throttling 으로 늘어난 간격을 보정한다).
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
  }

  /**
   * 현재 세션의 업로드를 로컬에서 중단하고 브라우저 리소스를 해제한다.
   *
   * 서버 작업은 취소하지 않는다. 브라우저가 정상 종료되지 못해도 서버 lease 는 만료된다. 사용자는
   * 다음 세션에서 폴더를 다시 선택해 명시적으로 이어받을 수 있다.
   */
  disposeSession(): void {
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
    this.sessionActive = false;
    this.sessionGeneration += 1;
    this.stopHeartbeat();
    for (const job of this.jobs) {
      job.stopIntent = "pause";
      job.abortController?.abort();
    }
    for (const timer of this.pollTimers.values()) {
      clearTimeout(timer);
    }
    this.pollTimers.clear();
    this.jobs = [];
    this.activeJobId = null;
    this.leaseBlocked = false;
    this.emit();
  }

  getSnapshot = (): UploadEngineSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // 작업을 큐에 넣고(상태 queued) 펌프를 실행한다. job id 를 즉시 반환해 호출부가 모달을 닫게 한다.
  enqueue = (spec: UploadJobSpec): string => {
    if (!this.sessionActive) {
      throw new Error("Upload session is not active.");
    }
    const id = crypto.randomUUID();
    const files = specFiles(spec).map(toPendingFileState);
    const job: InternalJob = {
      id,
      spec,
      status: "queued",
      finalize: { status: "idle", errorMessage: null },
      uploadJob: specUploadJob(spec),
      files,
      inFlightLoaded: new Map(),
      orphans: [],
      progress: aggregateProgress(files, new Map()),
      errorMessage: null,
      abortController: null,
      stopIntent: null,
      initialized: false,
      stagingDiffRequired: false,
      finalizeExpectation: {},
      connection: "ok",
    };
    this.jobs = [...this.jobs, job];
    this.emit();
    this.pump();
    return id;
  };

  // 전송을 로컬에서 멈춘다. 서버 잡과 staged 바이트는 그대로 남아 언제든 재개할 수 있다.
  pause = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    if (job.status === "queued") {
      this.patchJob(jobId, { status: "paused" });
      return;
    }
    if (job.id === this.activeJobId && isTransferStatus(job.status)) {
      job.stopIntent = "pause";
      this.stopHeartbeat();
      job.abortController?.abort();
    }
  };

  // 일시정지한 잡을 다시 큐에 넣는다. 남은(pending) 파일부터 이어서 올린다.
  resume = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined || job.status !== "paused") {
      return;
    }
    this.patchJob(jobId, { status: "queued", errorMessage: null, connection: "ok" });
    this.pump();
  };

  // 서버 잡을 취소한다(PENDING_UPLOAD/UPLOADED 만 가능). 전송 중이면 먼저 중단하고 취소한다.
  cancel = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    if (job.uploadJob === null) {
      // 서버 잡이 아직 없다(대기 중 취소). 큐에서 제거하면 충분하다.
      this.removeJob(jobId);
      return;
    }
    if (
      job.status === "blocked" ||
      (job.uploadJob.status !== "pending_upload" && job.uploadJob.status !== "uploaded")
    ) {
      return;
    }
    if (job.id === this.activeJobId && isTransferStatus(job.status)) {
      job.stopIntent = "cancel";
      this.stopHeartbeat();
      job.abortController?.abort();
      return;
    }
    void this.cancelOnServer(jobId, job.uploadJob.uuid);
  };

  // awaitingComplete 잡의 명시적 완료. 현황 페이지의 분리된 staged 검토(서버 잡)가 쓴다. 업로드 창
  // 흐름은 전송 후 항상 자동으로 finalize 하므로 이 경로를 쓰지 않는다.
  complete = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined || job.status !== "awaitingComplete" || job.uploadJob === null) {
      return;
    }
    // 검토에서 staged 파일을 지웠을 수 있다. 그래서 선택 폴더 기준의 기대값을 보내지 않는다.
    void this.runComplete(jobId, job.uploadJob.uuid);
  };

  // finalize(complete) 실패 아이템의 재시도. complete 호출만 다시 시도한다(전송은 이미 끝났다).
  retryFinalize = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined || job.finalize.status !== "failed" || job.uploadJob === null) {
      return;
    }
    void this.runComplete(jobId, job.uploadJob.uuid, job.finalizeExpectation);
  };

  // 실패한 파일 1개만 다시 올린다(그 파일을 pending 으로 되돌리고 큐에 다시 넣는다). 실패 상태를
  // 해제하고 전송을 이어 가면 finishAfterDrain 이 다시 finalize 를 시도한다.
  //
  // 잡이 failed 일 때만 받는다. 전송 중인 잡을 queued 로 되돌리면 같은 잡의 실행이 두 개가 된다.
  // 앞 실행은 뒤 실행의 파일이 올라가기 전에 complete 를 호출한다(ZERG-1042).
  retryFile = (jobId: string, relativePath: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined || job.status !== "failed") {
      return;
    }
    const target = job.files.find((file) => file.relativePath === relativePath);
    if (target === undefined || target.status !== "failed") {
      return;
    }
    job.files = job.files.map((file) =>
      file.relativePath === relativePath
        ? { ...file, status: "pending", errorMessage: null }
        : file,
    );
    job.finalize = { status: "idle", errorMessage: null };
    job.inFlightLoaded = new Map();
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.patchJob(jobId, { status: "queued", errorMessage: null, stopIntent: null });
    this.pump();
  };

  // 실패/차단/취소 잡을 다시 큐에 넣는다. 서버 상태와 로컬 scan 보유 여부로 경로를 고른다.
  retry = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined || !retryableStatuses.includes(job.status)) {
      return;
    }
    const rebuilt = buildRetrySpec(job);
    if (rebuilt === null) {
      // scan 이 없는 reprocess 실패 등은 폴더 재선택(재개 경로)이 필요하다. 그래서 여기서는 다시 큐에 넣지 않는다.
      return;
    }
    if (rebuilt === job.spec) {
      // 클라이언트 실패 이어 올리기: 실패 파일만 pending 으로 되돌린다(성공·건너뜀 파일은 유지).
      job.files = job.files.map(failedToPending);
    } else {
      job.spec = rebuilt;
      if (rebuilt.kind === "reset") {
        // 새 staging prefix 로 모든 파일을 다시 전송한다. 파일 상태와 준비 단계를 처음으로 되돌린다.
        job.files = specFiles(rebuilt).map(toPendingFileState);
        job.orphans = [];
        job.initialized = false;
      }
    }
    job.inFlightLoaded = new Map();
    job.finalize = { status: "idle", errorMessage: null };
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.patchJob(jobId, { status: "queued", errorMessage: null, stopIntent: null });
    this.pump();
  };

  // terminal(또는 일시정지) 잡을 목록에서 제거한다. 서버 잡은 바꾸지 않는다. pending_upload 로
  // 남은 잡은 현황 페이지의 재개 경로가 이어받는다.
  dismiss = (jobId: string): void => {
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    if (terminalStatuses.includes(job.status) || job.status === "paused") {
      this.removeJob(jobId);
    }
  };

  // ── 큐 펌프 ──

  private pump(): void {
    if (!this.sessionActive || this.activeJobId !== null) {
      return;
    }
    const next = this.jobs.find((job) => job.status === "queued");
    if (next === undefined) {
      return;
    }
    this.activeJobId = next.id;
    void this.runJob(next.id, this.sessionGeneration);
  }

  private async runJob(jobId: string, sessionGeneration: number): Promise<void> {
    const abortController = new AbortController();
    this.leaseBlocked = false;
    this.patchJob(jobId, { abortController, stopIntent: null, connection: "ok" });
    const job = this.findJob(jobId);
    if (job === undefined) {
      this.releaseSlot(jobId);
      return;
    }
    let drained = false;
    try {
      // 준비 단계(init/reset/probe)는 잡당 1회만 한다. 일시정지 후 재개는 전송만 이어 간다.
      if (!job.initialized) {
        switch (job.spec.kind) {
          case "new":
            await this.runInitNew(jobId, job.spec, abortController.signal);
            break;
          case "resume":
            await this.runReconcile(jobId, job.spec, abortController.signal);
            break;
          case "reset":
            await this.runReset(jobId, job.spec, abortController.signal);
            break;
          case "reprocess":
            break;
        }
        this.patchJob(jobId, { initialized: true });
      }
      if (job.spec.kind === "reprocess") {
        await this.runReprocess(jobId, job.spec.uploadJob.uuid);
      } else {
        drained = await this.runTransfer(jobId, abortController.signal);
      }
    } catch (error) {
      if (sessionGeneration === this.sessionGeneration) {
        await this.handleRunError(jobId, abortController, error);
      }
    } finally {
      if (sessionGeneration === this.sessionGeneration) {
        this.stopHeartbeat();
        this.releaseSlot(jobId);
      }
    }
    if (drained && sessionGeneration === this.sessionGeneration) {
      await this.finishAfterDrain(jobId);
    }
  }

  // activeJobId 를 먼저 비운 뒤 emit 해야 스냅샷이 최신 활성 상태를 반영한다. pump 가 다음 잡을
  // 찾지 못하면 추가 emit 이 없으므로, 여기서 비운 값이 마지막 스냅샷이 된다.
  private releaseSlot(jobId: string): void {
    if (this.activeJobId !== jobId) {
      return;
    }
    this.activeJobId = null;
    this.patchJob(jobId, { abortController: null });
    this.pump();
  }

  // ── 실행 경로 ──

  private async runInitNew(
    jobId: string,
    spec: Extract<UploadJobSpec, { kind: "new" }>,
    signal: AbortSignal,
  ): Promise<void> {
    this.patchJob(jobId, { status: "initializing" });
    const initialized = await this.api.init({
      display_name: spec.displayName,
      embodiment_uuid: spec.embodimentUuid,
      tip_uuid: spec.tipUuid,
      data_origin: spec.dataOrigin,
      band_diagnostics_enabled: spec.bandDiagnosticsEnabled,
      // null 은 키가 없는 것과 같은 뜻(조직 기본 위치)이라 그대로 보낸다. 계약은 @/api 가 소유한다.
      upload_location_code: spec.uploadLocationCode,
      target_backend: "gcp_storage",
    });
    throwIfUploadAborted(signal);
    // 잡이 생성되었다. 이후 단계가 실패하면 재시도(reset) 경로로 갈 수 있게 즉시 기록한다.
    this.patchJob(jobId, { uploadJob: initialized });
    await this.api.setReservedLabels(initialized.uuid, spec.reservedLabels);
    throwIfUploadAborted(signal);
  }

  private async runReset(
    jobId: string,
    spec: Extract<UploadJobSpec, { kind: "reset" }>,
    signal: AbortSignal,
  ): Promise<void> {
    this.patchJob(jobId, { status: "initializing" });
    // 조립은 wire 경계가 소유한다. 폼의 null 을 그대로 보내면 "유지" 와 "비움" 을 구분할 수 없다.
    const reset = await this.api.reset(
      spec.uploadJob.uuid,
      buildDatasetUploadResetReq(spec.uploadJob, {
        reservedLabels: spec.reservedLabels,
        embodimentUuid: spec.embodimentUuid,
        tipUuid: spec.tipUuid,
        dataOrigin: spec.dataOrigin,
        bandDiagnosticsEnabled: spec.bandDiagnosticsEnabled,
      }),
    );
    throwIfUploadAborted(signal);
    this.patchJob(jobId, { uploadJob: reset });
  }

  // 재개: staged 실측(probe) 1회 → path+size diff → 일치하는 파일 skipped 표시 → 고아 파일 정리.
  private async runReconcile(
    jobId: string,
    spec: Extract<UploadJobSpec, { kind: "resume" }>,
    signal: AbortSignal,
  ): Promise<void> {
    this.patchJob(jobId, { status: "reconciling", uploadJob: spec.uploadJob });
    const diff = await this.diffAgainstStaging(spec.uploadJob.uuid, spec.scan.files, signal);
    const skippedPaths = new Set(diff.skipped.map((file) => file.relativePath));
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    job.files = job.files.map((file) =>
      skippedPaths.has(file.relativePath) && file.status === "pending"
        ? { ...file, status: "skipped" }
        : file,
    );
    await this.settleOrphans(jobId, spec.uploadJob.uuid, diff.orphans, signal);
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.emit();
  }

  // staged 전체 목록을 선택 폴더와 path+size 로 대조한다. 서버는 페이지마다 prefix 를 다시 나열한다.
  // 그래서 경로 단위의 판정이 필요한 두 자리(재개 시작, complete 직전의 건수 불일치)에서만 호출한다.
  private async diffAgainstStaging(
    uploadUuid: string,
    scanned: readonly ScannedFile[],
    signal: AbortSignal,
  ): Promise<StagedDiff> {
    const staged = await fetchAllStagedFiles(
      (query) => this.api.listStagedFiles(uploadUuid, query),
      this.config.probePageSize,
    );
    throwIfUploadAborted(signal);
    return diffStagedFiles(scanned, staged);
  }

  // 선택 폴더에 없는 staged 파일을 정리한다. 기본은 삭제다("선택 폴더가 기준"). complete 는 prefix
  // 전체를 Dataset 으로 확정한다. 그래서 서버에만 남은 파일은 complete 전에 지운다.
  private async settleOrphans(
    jobId: string,
    uploadUuid: string,
    orphans: readonly StagedFile[],
    signal: AbortSignal,
  ): Promise<void> {
    if (this.config.pruneOrphans) {
      for (const orphan of orphans) {
        throwIfUploadAborted(signal);
        await this.api.deleteStagedFile(uploadUuid, orphan.relative_path);
      }
    }
    const job = this.findJob(jobId);
    if (job !== undefined) {
      job.orphans = this.config.pruneOrphans ? [] : [...orphans];
    }
  }

  private async runReprocess(jobId: string, uploadUuid: string): Promise<void> {
    this.patchJob(jobId, { status: "initializing" });
    const reprocessed = await this.api.reprocess(uploadUuid);
    this.applyServerJob(jobId, reprocessed);
  }

  // declare(lazy 배치)→signed PUT 풀→staged 건수 확인. true 를 반환하면 finishAfterDrain 이 이어받는다.
  // 그때 실패 파일이 남아 있으면 부분 실패이고, 없으면 staged 건수가 선택 폴더와 같다.
  //
  // 건수가 다르면 staged 전체 목록에서 빠진 파일을 찾아 다시 올린다(stagingRepairRounds 회까지).
  // 그 뒤에도 다르면 잡을 failed 로 두고 false 를 반환한다. complete 는 호출하지 않는다.
  private async runTransfer(jobId: string, signal: AbortSignal): Promise<boolean> {
    const job = this.findJob(jobId);
    if (job === undefined || job.uploadJob === null) {
      return false;
    }
    const uploadUuid = job.uploadJob.uuid;
    const scanned = specFiles(job.spec);
    const scannedByPath = new Map(scanned.map((file) => [file.relativePath, file] as const));
    const pendingFiles = (): ScannedFile[] =>
      (this.findJob(jobId)?.files ?? [])
        .filter((file) => file.status === "pending")
        .map((file) => scannedByPath.get(file.relativePath))
        .filter((file): file is ScannedFile => file !== undefined);
    assertStagingObjectNamesWithinLimit(
      job.uploadJob.staging_object_key,
      pendingFiles().map((file) => file.relativePath),
    );
    this.patchJob(jobId, { status: "uploading" });
    // 티켓 저장소의 수명은 전송 1회다. 일시정지 후 재개하면 새로 선언한다(재선언은 재발급이라 안전하다).
    const ticketStore = new UploadTicketStore((files) => this.api.declareFiles(uploadUuid, files), {
      declareBatchSize: this.config.declareBatchSize,
      expiryMarginMs: this.config.ticketExpiryMarginMs,
    });
    this.startHeartbeat(uploadUuid);
    for (let repairRound = 0; ; repairRound += 1) {
      await this.runPutRounds(jobId, ticketStore, pendingFiles, signal);
      const current = this.findJob(jobId);
      if (current?.files.some((file) => file.status === "failed") === true) {
        return true;
      }
      const diffRequired = current?.stagingDiffRequired === true;
      if (!diffRequired && (await this.stagedCountMatches(jobId, uploadUuid, signal))) {
        return true;
      }
      if (repairRound >= this.config.stagingRepairRounds) {
        this.patchJob(jobId, { status: "failed", errorMessage: stagingCountMismatchMessage });
        return false;
      }
      await this.repairFromStaging(jobId, uploadUuid, scanned, signal);
    }
  }

  // pending 파일을 올린다. 풀이 끝났을 때 실패 파일이 있으면 그 파일만 다시 올린다(autoRetryRounds
  // 회까지). 그 뒤에도 남은 실패는 finishAfterDrain 이 부분 실패로 세운다. 그때 사용자가 파일 단위로
  // 재시도한다.
  private async runPutRounds(
    jobId: string,
    ticketStore: UploadTicketStore,
    pendingFiles: () => ScannedFile[],
    signal: AbortSignal,
  ): Promise<void> {
    for (let retryRound = 0; ; retryRound += 1) {
      if (retryRound > 0) {
        // 바로 다시 보내면 429 같은 거부를 같은 이유로 다시 받는다. 그래서 라운드마다 두 배씩 기다린다.
        // 일시정지·취소는 이 대기도 바로 끊는다.
        await delayUnlessAborted(this.config.putBackoffBaseMs * 2 ** retryRound, signal);
      }
      const pending = pendingFiles();
      if (pending.length === 0) {
        return;
      }
      await this.runPutRound(jobId, ticketStore, pending, signal);
      if (retryRound >= this.config.autoRetryRounds || !this.requeueFailedFiles(jobId)) {
        return;
      }
    }
  }

  // complete 직전 확인이다. staged 건수를 요청 1회로 읽고 기대 건수와 비교한다. 기대 건수는 선택 폴더의
  // 파일 수에, 지우지 않고 남긴 orphan 수를 더한 값이다.
  private async stagedCountMatches(
    jobId: string,
    uploadUuid: string,
    signal: AbortSignal,
  ): Promise<boolean> {
    const page = await this.api.listStagedFiles(uploadUuid, { page: 1, size: 1 });
    throwIfUploadAborted(signal);
    const job = this.findJob(jobId);
    return job !== undefined && page.total === expectedStagedCount(job);
  }

  // staged 건수가 다를 때만 실행한다. staged 에 없거나 크기가 다른 파일을 pending 으로 되돌린다.
  // 선택 폴더에 없는 staged 파일은 재개와 같은 정책으로 정리한다.
  private async repairFromStaging(
    jobId: string,
    uploadUuid: string,
    scanned: readonly ScannedFile[],
    signal: AbortSignal,
  ): Promise<void> {
    this.patchJob(jobId, { status: "reconciling" });
    const diff = await this.diffAgainstStaging(uploadUuid, scanned, signal);
    await this.settleOrphans(jobId, uploadUuid, diff.orphans, signal);
    const missingPaths = new Set(diff.toUpload.map((file) => file.relativePath));
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    job.files = job.files.map((file) =>
      missingPaths.has(file.relativePath)
        ? { ...file, status: "pending", errorMessage: null }
        : file,
    );
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.patchJob(jobId, { status: "uploading", stagingDiffRequired: false });
  }

  // PUT 을 한 라운드 실행한다. 대상 파일을 다시 선언하고 풀로 올린다. 한 파일이 실패해도 나머지
  // 파일을 끝까지 올린다. 실패는 파일 상태에 남긴다.
  private async runPutRound(
    jobId: string,
    ticketStore: UploadTicketStore,
    pending: readonly ScannedFile[],
    signal: AbortSignal,
  ): Promise<void> {
    ticketStore.prime(pending);
    await runPutPool(
      pending,
      {
        concurrency: this.config.fileConcurrency,
        maxAttempts: this.config.putMaxAttempts,
        backoffBaseMs: this.config.putBackoffBaseMs,
        stallTimeoutMs: this.config.putStallTimeoutMs,
        reconnectBackoffMaxMs: this.config.reconnectBackoffMaxMs,
        offlineGraceMs: this.config.offlineGraceMs,
        progressEvidenceAfterMs: this.config.progressEvidenceAfterMs,
        maxInterruptions: this.config.putMaxInterruptions,
        maxOfflineMs: this.config.maxOfflineMs,
      },
      {
        ticketFor: (file) => ticketStore.ticketFor(file.relativePath),
        invalidateTicket: (relativePath) => ticketStore.invalidate(relativePath),
        onFileStart: (relativePath) =>
          this.patchFile(jobId, relativePath, (file) => ({
            ...file,
            status: "uploading",
            attempts: file.attempts + 1,
            errorMessage: null,
          })),
        onFileProgress: (relativePath, loaded) =>
          this.handleFileProgress(jobId, relativePath, loaded),
        onFileUploaded: (relativePath) => {
          this.findJob(jobId)?.inFlightLoaded.delete(relativePath);
          this.patchFile(jobId, relativePath, (file) => ({ ...file, status: "uploaded" }));
        },
        onFileFailed: (relativePath, error) => {
          this.findJob(jobId)?.inFlightLoaded.delete(relativePath);
          this.patchFile(jobId, relativePath, (file) => ({
            ...file,
            status: "failed",
            errorMessage: resolveUploadErrorMessage(error),
          }));
        },
        onConnectionChange: (waiting) =>
          this.patchJob(jobId, { connection: waiting ? "waiting" : "ok" }),
      },
      signal,
    );
  }

  // 실패 파일을 pending 으로 되돌린다. 되돌린 파일이 없으면 false 를 반환한다.
  private requeueFailedFiles(jobId: string): boolean {
    const job = this.findJob(jobId);
    if (job === undefined || !job.files.some((file) => file.status === "failed")) {
      return false;
    }
    job.files = job.files.map(failedToPending);
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.emit();
    return true;
  }

  // 전송 드레인 이후: 실패 파일이 있으면 부분 실패로 설정한다(파일 단위 재시도). 없으면 항상 finalize 로
  // 이어 간다(수동 완료 게이트 없음). 이 단계는 전송 슬롯 밖에서 실행된다(다음 잡이 이미 전송 중일 수 있다).
  private async finishAfterDrain(jobId: string): Promise<void> {
    const job = this.findJob(jobId);
    if (job === undefined || job.status !== "uploading") {
      return;
    }
    const failedCount = job.files.filter((file) => file.status === "failed").length;
    if (failedCount > 0) {
      this.patchJob(jobId, {
        status: "failed",
        errorMessage: `${failedCount}개 파일 업로드에 실패했습니다. 실패한 파일을 다시 올려 주세요.`,
      });
      return;
    }
    // complete 는 서버가 그 시점의 staging 을 Dataset 으로 확정하게 한다. 그래서 "실패 0" 이 아니라
    // "모든 파일이 올라갔다" 를 조건으로 둔다. 올라가지 않은 파일이 하나라도 있으면 complete 하지 않는다.
    //
    // 지금 흐름에서는 여기에 오지 않는다. 오면 잡을 failed 로 둔다. 그대로 두면 전송 슬롯은 비었는데 잡은
    // uploading 에 남는다. 그 잡은 일시정지도 닫기도 받지 않는다. failed 면 재시도가 남은 파일을 올린다.
    if (!job.files.every(isSettledFile)) {
      this.patchJob(jobId, { status: "failed", errorMessage: unsettledFilesMessage });
      return;
    }
    await this.runComplete(jobId, mustUploadUuid(job), completeExpectation(job));
  }

  // finalize(complete 호출). finalize 아이템 상태도 함께 전이해 오른쪽 목록에 전송과 같은 레이어로
  // 표시한다(실패하면 재시도 대상). 전송 슬롯 밖에서 실행된다.
  private async runComplete(
    jobId: string,
    uploadUuid: string,
    expectation: DatasetUploadCompleteReq = {},
  ): Promise<void> {
    this.patchJob(jobId, {
      status: "completing",
      errorMessage: null,
      finalize: { status: "running", errorMessage: null },
    });
    try {
      const completed = await this.api.complete(uploadUuid, expectation);
      this.patchJob(jobId, { finalize: { status: "done", errorMessage: null } });
      this.applyServerJob(jobId, completed);
    } catch (error) {
      if (isUploadLeaseHeld(error)) {
        this.patchJob(jobId, {
          status: "blocked",
          errorMessage: uploadBlockedMessage,
          finalize: { status: "failed", errorMessage: uploadBlockedMessage },
        });
        return;
      }
      // 상태 드리프트(다른 창에서 이미 완료 등)면 최신 서버 상태를 따른다. 기대값 불일치(409)도 먼저
      // 여기서 가른다. 서버는 이미 uploaded 인 잡에서도 기록한 집계가 기대값과 다르면 같은 409 를 준다.
      // 그 잡에는 파일을 다시 올릴 수 없다.
      const latest = await this.tryGetJob(uploadUuid);
      if (latest !== null && latest.status !== "pending_upload") {
        this.patchJob(jobId, { finalize: { status: "done", errorMessage: null } });
        this.applyServerJob(jobId, latest);
        return;
      }
      if (latest !== null && isCompleteStagingMismatch(error)) {
        // 서버 잡은 pending_upload 이고 staging 실측이 기대값과 다르다. finalize 를 idle 로 두어 잡
        // 재시도(retry)로 보낸다. complete 만 다시 호출하면 같은 거부를 받는다. 서버 잡을 읽지 못했으면
        // 이 분기로 오지 않는다. 그때는 아래에서 complete 재시도만 남긴다.
        this.patchJob(jobId, {
          status: "failed",
          errorMessage: resolveUploadErrorMessage(error),
          finalize: { status: "idle", errorMessage: null },
          stagingDiffRequired: true,
        });
        return;
      }
      // finalize 실패(413 용량 초과 포함). finalize 아이템을 실패로 설정해 재시도할 수 있게 한다.
      const overLimit = isCompleteOverLimit(error);
      const message = overLimit
        ? resolveUploadErrorMessage(error) + " 스테이징 파일을 정리한 뒤 다시 시도해 주세요."
        : resolveUploadErrorMessage(error);
      this.patchJob(jobId, {
        status: "failed",
        errorMessage: message,
        finalize: { status: "failed", errorMessage: message },
        // 용량 초과는 사용자가 staged 파일을 지워서 해결한다. 그 뒤에는 선택 폴더가 staging 의 기준이
        // 아니다. 그래서 다음 complete 는 기대값을 싣지 않는다.
        finalizeExpectation: overLimit ? {} : expectation,
      });
    }
  }

  private async cancelOnServer(jobId: string, uploadUuid: string): Promise<void> {
    try {
      const cancelled = await this.api.cancel(uploadUuid);
      this.applyServerJob(jobId, cancelled);
    } catch (error) {
      // 이미 워커가 가져갔거나 종료된 잡이다. 최신 상태를 따르고 사유를 남긴다.
      const latest = await this.tryGetJob(uploadUuid);
      if (latest !== null) {
        this.applyServerJob(jobId, latest);
      }
      this.patchJob(jobId, { errorMessage: resolveUploadErrorMessage(error) });
    }
  }

  // 서버 잡 응답을 로컬 잡 상태에 반영한다(완료/실패/취소/처리 중 판정 + 폴링 예약).
  private applyServerJob(jobId: string, serverJob: DatasetUploadJob): void {
    this.patchJob(jobId, {
      uploadJob: serverJob,
      status: engineStatusForServer(serverJob),
      errorMessage: serverTerminalErrorMessage(serverJob),
      // 잡이 서버 상태를 따른다. 단절로 멈춘 채 재개를 기다리는 잡이 아니다.
      connection: "ok",
    });
    if (this.findJob(jobId)?.status === "processing") {
      this.schedulePoll(jobId, serverJob.uuid, 0);
    } else {
      this.stopPolling(jobId);
    }
  }

  private async handleRunError(
    jobId: string,
    abortController: AbortController,
    error: unknown,
  ): Promise<void> {
    this.stopHeartbeat();
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    const intent = job.stopIntent;
    job.stopIntent = null;
    // 전송이 끝났다. 단절 대기도 끝났다. 아래의 한도 초과 분기만 lost 로 바꾼다.
    job.connection = "ok";
    if (this.leaseBlocked || isUploadLeaseHeld(error)) {
      this.leaseBlocked = false;
      this.resetInFlightFiles(jobId);
      this.patchJob(jobId, { status: "blocked", errorMessage: uploadBlockedMessage });
      return;
    }
    if (abortController.signal.aborted) {
      this.resetInFlightFiles(jobId);
      if (intent === "cancel" && job.uploadJob !== null) {
        await this.cancelOnServer(jobId, job.uploadJob.uuid);
        return;
      }
      this.patchJob(jobId, { status: "paused", errorMessage: abortedMessage });
      return;
    }
    if (error instanceof UploadConnectionLostError) {
      // 파일의 실패가 아니다. 연결이 돌아온 뒤 사용자가 재개하면 남은 파일부터 이어 올린다. 그래서
      // failed 가 아니라 paused 로 둔다.
      this.resetInFlightFiles(jobId);
      this.patchJob(jobId, {
        status: "paused",
        errorMessage: connectionLostMessage(this.config.maxOfflineMs),
        connection: "lost",
      });
      return;
    }
    this.resetInFlightFiles(jobId);
    this.patchJob(jobId, { status: "failed", errorMessage: resolveUploadErrorMessage(error) });
  }

  // 중단된 전송의 uploading 파일을 pending 으로 되돌린다(one-shot PUT 은 부분 재개가 없다).
  private resetInFlightFiles(jobId: string): void {
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    job.inFlightLoaded = new Map();
    job.files = job.files.map((file) =>
      file.status === "uploading" ? { ...file, status: "pending" } : file,
    );
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.emit();
  }

  // ── lease heartbeat (활성 전송 전용. complete 는 호출 자체가 lease 를 다시 얻는다) ──

  private startHeartbeat(uploadUuid: string): void {
    this.stopHeartbeat();
    this.heartbeatUuid = uploadUuid;
    const sessionGeneration = this.sessionGeneration;
    this.heartbeatTimer = setInterval(() => {
      void this.runHeartbeat(uploadUuid, sessionGeneration);
    }, this.config.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    this.heartbeatUuid = null;
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private readonly handleVisibilityChange = (): void => {
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "visible" &&
      this.heartbeatUuid !== null
    ) {
      void this.runHeartbeat(this.heartbeatUuid, this.sessionGeneration);
    }
  };

  private async runHeartbeat(uploadUuid: string, sessionGeneration: number): Promise<void> {
    try {
      await this.api.heartbeat(uploadUuid);
    } catch (error) {
      if (sessionGeneration !== this.sessionGeneration) {
        return;
      }
      if (isUploadLeaseHeld(error)) {
        this.leaseBlocked = true;
        this.stopHeartbeat();
        if (this.activeJobId !== null) {
          this.findJob(this.activeJobId)?.abortController?.abort();
        }
      }
    }
  }

  // ── complete 이후 서버 파이프라인 폴링 ──

  private schedulePoll(jobId: string, uploadUuid: string, delayMs: number): void {
    this.stopPolling(jobId);
    const timer = setTimeout(() => {
      this.pollTimers.delete(jobId);
      void this.pollServerJob(jobId, uploadUuid);
    }, delayMs);
    this.pollTimers.set(jobId, timer);
  }

  private stopPolling(jobId: string): void {
    const timer = this.pollTimers.get(jobId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.pollTimers.delete(jobId);
    }
  }

  private async pollServerJob(jobId: string, uploadUuid: string): Promise<void> {
    if (this.findJob(jobId)?.status !== "processing") {
      return;
    }
    try {
      const latest = await this.api.get(uploadUuid);
      this.applyServerJob(jobId, latest);
      if (this.findJob(jobId)?.status === "processing") {
        this.schedulePoll(jobId, uploadUuid, this.config.pollIntervalMs);
      }
    } catch {
      if (this.findJob(jobId)?.status === "processing") {
        this.schedulePoll(jobId, uploadUuid, this.config.pollIntervalMs);
      }
    }
  }

  private async tryGetJob(uploadUuid: string): Promise<DatasetUploadJob | null> {
    try {
      return await this.api.get(uploadUuid);
    } catch {
      return null;
    }
  }

  // ── 스토어 헬퍼 ──

  private findJob(jobId: string): InternalJob | undefined {
    return this.jobs.find((job) => job.id === jobId);
  }

  private removeJob(jobId: string): void {
    this.stopPolling(jobId);
    this.jobs = this.jobs.filter((job) => job.id !== jobId);
    this.emit();
  }

  private patchJob(jobId: string, patch: Partial<InternalJob>): void {
    let changed = false;
    this.jobs = this.jobs.map((job) => {
      if (job.id !== jobId) {
        return job;
      }
      changed = true;
      return { ...job, ...patch };
    });
    if (changed) {
      this.emit();
    }
  }

  private patchFile(
    jobId: string,
    relativePath: string,
    update: (file: UploadFileState) => UploadFileState,
  ): void {
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    job.files = job.files.map((file) => (file.relativePath === relativePath ? update(file) : file));
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.emit();
  }

  private handleFileProgress(jobId: string, relativePath: string, loaded: number): void {
    const job = this.findJob(jobId);
    if (job === undefined) {
      return;
    }
    job.inFlightLoaded.set(relativePath, loaded);
    job.progress = aggregateProgress(job.files, job.inFlightLoaded);
    this.emit();
  }

  private emit(): void {
    this.snapshot = {
      jobs: this.jobs.map(toPublicJob),
      activeJobId: this.activeJobId,
      hasPendingWork: this.jobs.some((job) => clientPendingStatuses.includes(job.status)),
    };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function isTransferStatus(status: UploadEngineJobStatus): boolean {
  return status === "initializing" || status === "reconciling" || status === "uploading";
}

// 파일이 staging 에 있는지 판정한다. 이번 전송이 올린 파일(uploaded)과, 재개 대조가 이미 있다고
// 판정한 파일(skipped)이 해당한다.
function isSettledFile(file: UploadFileState): boolean {
  return file.status === "uploaded" || file.status === "skipped";
}

// staging 에 있어야 하는 파일 수다. 선택 폴더의 파일 수에, 지우지 않고 남긴 orphan 수를 더한다.
function expectedStagedCount(job: InternalJob): number {
  return job.files.length + job.orphans.length;
}

// complete 본문의 기대값이다. 건수의 기준은 complete 직전 확인과 같다. 남긴 orphan 중에 크기를 모르는
// 파일이 있으면 총 바이트를 계산할 수 없다. 그때는 건수만 보낸다.
function completeExpectation(job: InternalJob): DatasetUploadCompleteReq {
  const expected_file_count = expectedStagedCount(job);
  let expected_size_bytes = job.files.reduce((sum, file) => sum + file.sizeBytes, 0);
  for (const orphan of job.orphans) {
    if (orphan.size_bytes === null) {
      return { expected_file_count };
    }
    expected_size_bytes += orphan.size_bytes;
  }
  return { expected_file_count, expected_size_bytes };
}

// 실패 파일만 다시 올릴 대상으로 되돌린다. 다른 상태의 파일은 그대로 둔다.
function failedToPending(file: UploadFileState): UploadFileState {
  return file.status === "failed" ? { ...file, status: "pending", errorMessage: null } : file;
}

function engineStatusForServer(job: DatasetUploadJob): UploadEngineJobStatus {
  if (serverSuccessStatuses.includes(job.status)) {
    return "completed";
  }
  if (serverFailedStatuses.includes(job.status)) {
    return "failed";
  }
  if (job.status === "cancelled") {
    return "cancelled";
  }
  return "processing";
}

function serverTerminalErrorMessage(job: DatasetUploadJob): string | null {
  switch (job.status) {
    case "failed":
      return formatUploadJobFailure(job.failure, job.upload_verified);
    case "conflict":
      return conflictMessage;
    case "cancelled":
      return cancelledMessage;
    default:
      return null;
  }
}

function toPendingFileState(file: ScannedFile): UploadFileState {
  return {
    relativePath: file.relativePath,
    sizeBytes: file.sizeBytes,
    status: "pending",
    attempts: 0,
    errorMessage: null,
  };
}

function toPublicJob(job: InternalJob): UploadJobView {
  return {
    id: job.id,
    kind: job.spec.kind,
    displayName: specDisplayName(job.spec),
    status: job.status,
    finalize: job.finalize,
    uploadJob: job.uploadJob,
    progress: job.progress,
    files: job.files,
    orphans: job.orphans,
    errorMessage: job.errorMessage,
    connection: job.connection,
  };
}

function specDisplayName(spec: UploadJobSpec): string {
  if (spec.kind === "new") {
    return spec.displayName;
  }
  return spec.uploadJob.display_name;
}

function specFiles(spec: UploadJobSpec): readonly ScannedFile[] {
  return spec.kind === "reprocess" ? [] : spec.scan.files;
}

function specUploadJob(spec: UploadJobSpec): DatasetUploadJob | null {
  return spec.kind === "new" ? null : spec.uploadJob;
}

function mustUploadUuid(job: InternalJob): string {
  if (job.uploadJob === null) {
    throw new Error("Upload job is missing after transfer.");
  }
  return job.uploadJob.uuid;
}

// 재시도 명세를 다시 구성한다:
// - 서버 후처리 실패(검증 완료)는 reprocess 다.
// - 서버 terminal 은 reset 재업로드다.
// - 클라이언트 실패(잡은 pending_upload 유지)는 같은 spec 으로 이어 올린다.
// 입력 수정이 필요한 실패는 같은 scan 으로 해결할 수 없어 목록의 폴더 재선택 경로가 소유한다.
// 다시 구성할 수 없으면 null.
function buildRetrySpec(job: InternalJob): UploadJobSpec | null {
  const serverJob = job.uploadJob;
  if (requiresUploadSourceChange(serverJob?.failure)) {
    return null;
  }
  if (serverJob !== null && serverJob.status === "failed" && serverJob.upload_verified) {
    return { kind: "reprocess", uploadJob: serverJob };
  }
  const isServerTerminal =
    serverJob !== null && ["failed", "cancelled", "conflict"].includes(serverJob.status);
  if (isServerTerminal && job.spec.kind !== "reprocess") {
    return {
      kind: "reset",
      scan: job.spec.scan,
      uploadJob: serverJob,
      ...retryMetadata(job.spec, serverJob),
    };
  }
  return job.spec.kind === "reprocess" ? null : job.spec;
}

function retryMetadata(
  spec: Exclude<UploadJobSpec, { kind: "reprocess" }>,
  serverJob: DatasetUploadJob,
): UploadJobMetadata {
  if (spec.kind === "new" || spec.kind === "reset") {
    return {
      reservedLabels: spec.reservedLabels,
      embodimentUuid: spec.embodimentUuid,
      tipUuid: spec.tipUuid,
      dataOrigin: spec.dataOrigin,
      bandDiagnosticsEnabled: spec.bandDiagnosticsEnabled,
    };
  }
  // resume 은 메타를 서버 저장값에서 복원한다. null 해석은 api 경계 헬퍼가 소유한다.
  // 재업로드 다이얼로그와 같은 규칙이어야 진입점에 따라 기본값이 달라지지 않는다.
  return {
    reservedLabels: prefillReservedLabels(serverJob),
    embodimentUuid: serverJob.embodiment_uuid,
    tipUuid: serverJob.tip_uuid,
    dataOrigin: serverJob.data_origin,
    bandDiagnosticsEnabled: prefillBandDiagnosticsEnabled(serverJob),
  };
}

// 앱 범위 싱글턴. 모듈은 1회만 평가되므로 StrictMode 재마운트에도 같은 인스턴스를 유지한다.
export const uploadEngine = new UploadEngine();
